import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, ArrowRight, Cpu, BookOpen, MessageCircle, ClipboardList, Star, Quote, ShieldCheck, BadgeCheck, Heart, Download } from "lucide-react";
import { LegalFooter } from "@/components/LegalLayout";

// EDIT ME — Replace with real student testimonials as you collect them.
// Keep array length 3 for a clean 3-up grid.
const TESTIMONIALS = [
  {
    quote: "Brimi explains Java loops better than my coaching sir. The chat remembers what we discussed last time — feels like a real tutor.",
    name: "Aarav S.",
    meta: "Class X · ICSE · Delhi",
    color: "hsl(var(--brimi-blue))",
  },
  {
    quote: "The chapter-wise quizzes are a lifesaver before exams. I went from 62 to 81 in my prelim Computer Applications paper.",
    name: "Riya M.",
    meta: "Class IX · ICSE · Mumbai",
    color: "hsl(var(--brimi-coral))",
  },
  {
    quote: "I listen to the notes audio while travelling to school. The English Literature explanations are super clear.",
    name: "Kabir V.",
    meta: "Class XII · ISC · Bengaluru",
    color: "hsl(var(--brimi-mint))",
  },
];

const TRUST_BADGES = [
  { Icon: BadgeCheck, label: "ICSE & ISC latest syllabus" },
  { Icon: Sparkles, label: "Powered by Claude Sonnet 4.5" },
  { Icon: ShieldCheck, label: "20-day free trial · cancel anytime" },
  { Icon: Heart, label: "Made in India" },
];

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

const useCanInstall = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (isStandalone()) return undefined;
    if (typeof window === "undefined") return undefined;
    if (window.__BRIMI_INSTALL_READY__) setReady(true);
    const onReady = () => setReady(true);
    const onInstalled = () => setReady(false);
    window.addEventListener("brimi-install-ready", onReady);
    window.addEventListener("brimi-installed", onInstalled);
    return () => {
      window.removeEventListener("brimi-install-ready", onReady);
      window.removeEventListener("brimi-installed", onInstalled);
    };
  }, []);
  return ready;
};

const triggerInstall = async () => {
  if (typeof window.__brimiTriggerInstall !== "function") return;
  await window.__brimiTriggerInstall();
};

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canInstall = useCanInstall();

  useEffect(() => {
    if (user) navigate("/subjects", { replace: true });
  }, [user, navigate]);

  return (
    <div className="min-h-screen">
      <header className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[hsl(var(--brimi-blue))] to-[hsl(var(--brimi-coral))] flex items-center justify-center shadow-sm">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="font-black text-xl tracking-tight">Brimi AI</div>
        </div>
        <Link
          to="/login"
          data-testid="header-login-link"
          className="pill-btn bg-foreground text-background text-sm hover:bg-foreground/85"
        >
          Sign in
        </Link>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-12 pb-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[hsl(var(--brimi-yellow))]/30 border border-[hsl(var(--brimi-yellow))]/50 mb-6">
            <span className="w-2 h-2 rounded-full bg-[hsl(var(--brimi-coral))] animate-pulse" />
            <span className="text-xs font-bold uppercase tracking-wider">
              Built for ICSE & ISC students
            </span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05]">
            Your <span className="text-[hsl(var(--brimi-blue))]">AI study</span>
            <br />
            buddy for{" "}
            <span className="relative inline-block">
              <span className="relative z-10">Class IX–XII</span>
              <span className="absolute inset-x-0 bottom-1 h-3 bg-[hsl(var(--brimi-yellow))]/60 -z-0 rounded-sm" />
            </span>
          </h1>

          <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
            Chat with an AI tutor that actually knows your ICSE syllabus. Master
            Computer Applications, English and Mathematics with explanations,
            practice quizzes, board-style past papers and instant doubt-solving
            — chapter by chapter.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to="/login"
              data-testid="hero-cta-start"
              className="pill-btn bg-[hsl(var(--brimi-blue))] text-white hover:bg-[hsl(var(--brimi-blue))]/90 px-8 py-4 text-base shadow-lg shadow-[hsl(var(--brimi-blue))]/25"
            >
              Start learning free <ArrowRight className="w-5 h-5" />
            </Link>
            {canInstall && (
              <button
                type="button"
                onClick={triggerInstall}
                data-testid="hero-cta-install"
                className="pill-btn bg-[hsl(var(--brimi-yellow))] text-foreground hover:bg-[hsl(var(--brimi-yellow))]/90 px-6 py-4 text-base shadow-md"
              >
                <Download className="w-5 h-5" /> Install app
              </button>
            )}
            <Link
              to="/login"
              data-testid="hero-cta-signin"
              className="pill-btn bg-transparent text-foreground hover:bg-foreground/5 px-6 py-4 text-base border border-border"
            >
              I already have an account
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
            {[
              { n: "3", l: "Subjects" },
              { n: "4", l: "Classes" },
              { n: "100+", l: "Chapters" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-black text-3xl text-[hsl(var(--brimi-coral))]">
                  {s.n}
                </div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[hsl(var(--brimi-yellow))]/40 blur-2xl" />
            <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full bg-[hsl(var(--brimi-coral))]/30 blur-2xl" />

            <div className="relative tactile-card p-6 floaty">
              <div className="flex items-center gap-2 pb-4 border-b border-border">
                <div className="flex gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brimi-coral))]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brimi-yellow))]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brimi-mint))]" />
                </div>
                <div className="text-xs font-bold ml-auto text-muted-foreground">
                  Brimi AI · Class X · Java
                </div>
              </div>

              <div className="space-y-3 mt-4">
                <div className="flex justify-end">
                  <div className="bg-[hsl(var(--brimi-blue))] text-white px-4 py-2.5 rounded-2xl rounded-tr-sm max-w-[80%] text-sm">
                    What's a constructor in Java?
                  </div>
                </div>
                <div className="flex">
                  <div className="bg-muted px-4 py-2.5 rounded-2xl rounded-tl-sm max-w-[85%] text-sm leading-relaxed">
                    A <b>constructor</b> is a special method that runs when you
                    create an object. It sets up the object's initial state. In
                    Java it has the same name as the class and no return type:
                    <pre className="mt-2 text-xs bg-foreground/90 text-background p-2 rounded-lg overflow-x-auto">
                      {`class Student {
  Student() { /* init */ }
}`}
                    </pre>
                  </div>
                </div>
                <div className="flex">
                  <div className="bg-muted/60 px-4 py-2 rounded-2xl rounded-tl-sm text-sm text-muted-foreground italic">
                    ✨ Brimi is typing…
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              Icon: MessageCircle,
              title: "Doubt-solving chat",
              body: "Ask any chapter question. Get clear, step-by-step explanations in your syllabus's language.",
              color: "hsl(var(--brimi-blue))",
            },
            {
              Icon: ClipboardList,
              title: "AI-generated quizzes",
              body: "Practice with MCQs tailored to your chapter, with instant feedback and explanations.",
              color: "hsl(var(--brimi-coral))",
            },
            {
              Icon: Cpu,
              title: "Computer · English · Math",
              body: "Java & Python programming, OOP, literature, grammar, full Math syllabus — chapter-wise coverage for IX to XII.",
              color: "hsl(var(--brimi-mint))",
            },
          ].map(({ Icon, title, body, color }) => (
            <div key={title} className="tactile-card p-7">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                style={{ background: `${color}20`, color }}
              >
                <Icon className="w-6 h-6" />
              </div>
              <div className="font-bold text-xl mb-2">{title}</div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Social Proof + Testimonials */}
      <section
        data-testid="testimonials-section"
        className="max-w-6xl mx-auto px-6 pb-20"
      >
        {/* Trust badges row */}
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 pb-12">
          {TRUST_BADGES.map(({ Icon, label }) => (
            <div
              key={label}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/60 border border-border text-xs font-semibold text-muted-foreground"
            >
              <Icon className="w-3.5 h-3.5 text-[hsl(var(--brimi-blue))]" />
              {label}
            </div>
          ))}
        </div>

        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--brimi-coral))]/10 text-[hsl(var(--brimi-coral))] text-xs font-bold uppercase tracking-wider mb-4">
            <Star className="w-3.5 h-3.5 fill-current" />
            Loved by ICSE students
          </div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
            Students are{" "}
            <span className="relative inline-block">
              <span className="relative z-10">acing</span>
              <span className="absolute inset-x-0 bottom-1 h-3 bg-[hsl(var(--brimi-mint))]/60 -z-0 rounded-sm" />
            </span>{" "}
            their boards with Brimi
          </h2>
          <p className="text-sm text-muted-foreground mt-3 max-w-md mx-auto">
            Real students, real chapters, real results. Here&apos;s what early users
            are saying.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => {
            const initials = t.name
              .split(" ")
              .map((p) => p[0])
              .join("")
              .slice(0, 2)
              .toUpperCase();
            return (
              <div
                key={t.name}
                data-testid={`testimonial-${t.name.replace(/[^a-z]/gi, "").toLowerCase()}`}
                className="tactile-card p-6 flex flex-col"
              >
                <Quote
                  className="w-8 h-8 mb-3"
                  style={{ color: t.color }}
                  strokeWidth={1.5}
                />
                <p className="text-[15px] leading-relaxed flex-1">
                  {t.quote}
                </p>
                <div className="mt-5 pt-5 border-t border-border/60 flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center font-black text-sm text-white shadow-sm shrink-0"
                    style={{ background: t.color }}
                  >
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold text-sm truncate">{t.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {t.meta}
                    </div>
                  </div>
                  <div className="ml-auto flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-3.5 h-3.5 fill-[hsl(var(--brimi-yellow))] text-[hsl(var(--brimi-yellow))]"
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Final CTA strip */}
        <div
          data-testid="final-cta-strip"
          className="mt-16 tactile-card p-8 sm:p-10 bg-gradient-to-br from-[hsl(var(--brimi-blue))] to-[hsl(var(--brimi-coral))] text-white text-center"
        >
          <h3 className="text-2xl sm:text-3xl font-black tracking-tight">
            Start your 20-day free trial
          </h3>
          <p className="mt-2 text-sm sm:text-base text-white/90 max-w-md mx-auto">
            No credit card required. Cancel anytime. Built for ICSE &amp; ISC boards.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/login"
              data-testid="final-cta-btn"
              className="inline-flex items-center gap-2 pill-btn bg-white text-foreground hover:bg-white/90 px-7 py-3.5 text-base font-bold shadow-lg"
            >
              Try Brimi AI free <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(
                "Hey! Check out Brimi AI — an AI study buddy for ICSE & ISC Class IX–XII students. Chapter-wise chat, quizzes & notes powered by Claude 4.5. 20-day free trial: https://brimi.in"
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-share-btn"
              className="inline-flex items-center gap-2 pill-btn bg-[#25D366] text-white hover:bg-[#1ebe5a] px-6 py-3.5 text-base font-bold shadow-lg"
            >
              <svg
                viewBox="0 0 24 24"
                className="w-5 h-5 fill-current"
                aria-hidden="true"
              >
                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 2.09.62 4.03 1.69 5.66L2 22l4.6-1.21a9.83 9.83 0 0 0 5.44 1.65c5.46 0 9.91-4.45 9.91-9.92 0-2.65-1.03-5.14-2.91-7.01A9.82 9.82 0 0 0 12.04 2zm0 18.15c-1.62 0-3.21-.44-4.59-1.26l-.33-.2-3.41.9.91-3.33-.22-.34a8.18 8.18 0 0 1-1.27-4.41c0-4.53 3.69-8.21 8.21-8.21 2.19 0 4.25.85 5.8 2.4a8.17 8.17 0 0 1 2.41 5.81c0 4.53-3.69 8.21-8.21 8.21zm4.5-6.15c-.25-.12-1.46-.72-1.69-.8-.23-.08-.39-.12-.56.13-.16.25-.64.8-.78.96-.14.16-.29.18-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.48-1.39-1.73-.14-.25-.01-.39.11-.51.11-.11.25-.29.38-.43.13-.15.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.56-1.35-.76-1.85-.2-.49-.41-.42-.56-.43h-.48c-.16 0-.43.06-.66.31-.23.25-.86.84-.86 2.05 0 1.21.88 2.38 1 2.54.12.16 1.74 2.65 4.21 3.71.59.25 1.05.4 1.41.51.59.19 1.13.16 1.56.1.47-.07 1.46-.6 1.66-1.17.2-.58.2-1.07.14-1.17-.06-.1-.22-.16-.47-.28z" />
              </svg>
              Share with a friend
            </a>
          </div>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
}
